<template>
    <tr :class="[$style['mds-tr'], classObject]" :aria-expanded="ariaExpanded" :tabindex="tabIndex">
        <td :class="[$style['mds-tr__td--selection']]" v-if="multiselection && !isContainer">
            <mds-checkbox
                ref="checkbox"
                v-if="!isTotalNonExpandable"
                :label="multiselectionLabel"
                :value="multiselectionLabel"
                :indeterminate="indeterminate"
                v-model="isChecked"
                :on-dark="onDark"
                :size="size"
                hidden-label
                :disabled="disabled"
            />
        </td>
        <td :class="$style['mds-tr__td--numbered']" v-if="rowNumber && !isContainer">
            <span v-if="!isTotalNonExpandable">
                {{ rowNumber }}
            </span>
        </td>
        <slot />
    </tr>
</template>

<script>
    import MdsCheckbox from '@mds/checkbox-vue3';

    export default {
        name: 'MdsTr',
        components: {
            MdsCheckbox,
        },
        inject: ['onDark', 'size', 'rowNumbers', 'multiselection'],
        props: {
            checked: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description:
                        'If `true`, sets the multiselection checkbox to checked and applies styling for selected row.',
                },
            },
            disabled: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, disables the row multiselection checkbox.',
                },
            },
            expanded: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies styling for expanded.',
                },
            },
            indeterminate: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, sets the row multiselection checkbox to indeterminate state.',
                },
            },
            multiselectionLabel: {
                type: String,
                default: '',
                docs: {
                    validation: '—',
                    description: 'Applies the value to the multiselection checkbox label.',
                },
            },
            rowNumber: {
                type: [String, Number],
                docs: {
                    validation: '—',
                    description: 'Sets content for row number cell.',
                },
            },
            totalRow: {
                type: Boolean,
                default: false,
                docs: {
                    validation: '—',
                    description: 'If `true`, applies styling for total row.',
                },
            },
            type: {
                type: String,
                default: 'default',
                validator(value) {
                    return ['default', 'expandable', 'container'].indexOf(value) !== -1;
                },
                docs: {
                    validation: 'One of: `default`, `expandable`, `container`.',
                    description: 'Defines what type of row will be rendered.',
                },
            },
        },
        emits: ['mds-tr-multiselection-changed'],
        data() {
            return {
                isChecked: this.checked,
            };
        },
        computed: {
            ariaExpanded() {
                if (this.type !== 'expandable') {
                    return null;
                }
                return this.expanded ? 'true' : 'false';
            },
            tabIndex() {
                return this.type === 'expandable' ? 0 : null;
            },
            classObject() {
                const obj = {};
                obj[this.$style['mds-tr--selected']] = this.checked;
                obj[this.$style['mds-tr--total-row']] = this.totalRow;
                obj[this.$style['mds-tr--expandable']] = this.type === 'expandable';
                obj[this.$style['mds-tr--expanded']] = this.expanded;
                obj[this.$style['mds-tr--container']] = this.type === 'container';

                return obj;
            },
            isExpandable() {
                return this.type === 'expandable';
            },
            isContainer() {
                return this.type === 'container';
            },
            isTotalNonExpandable() {
                return this.type !== 'expandable' && this.totalRow;
            },
        },
        watch: {
            checked() {
                this.isChecked = this.checked;
                this.emitChangeEvent();
            },
            isChecked() {
                this.emitChangeEvent();
            },
        },
        methods: {
            emitChangeEvent() {
                this.$emit('mds-tr-multiselection-changed', this.isChecked);
            },
        },
    };
</script>

<style lang="scss" module>
    @use './tr.scss';
</style>
